import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon, ChevronDownIcon, FacebookIcon, IdCardIcon, LeafIcon, MapPinIcon, MenuIcon, SearchIcon, SmartphoneIcon, UserRoundIcon, XIcon, YoutubeIcon } from 'lucide-react';
import { Logo } from './Logo';
import { BellPreview } from './avis/BellPreview';
import { HeaderTooltip } from './HeaderTooltip';
import { AnimatePresence } from 'framer-motion';
import { navItems, utilityLinks } from '../data/navigation';

/** The bciti+ modules added to the Town's own header. */
const modules = [{
  label: 'Map',
  to: '/map',
  Icon: MapPinIcon
}, {
  label: 'My Card',
  to: '/my-card',
  Icon: IdCardIcon
}, {
  label: 'My Dashboard',
  to: '/my-dashboard',
  Icon: SmartphoneIcon
}, {
  label: 'My Profile',
  to: '/my-profile',
  Icon: UserRoundIcon
}];
const moduleButton = 'group relative grid h-10 w-10 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:bg-white/15 hover:text-ville-spring focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring';
export function Header() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  return <header className="w-full" data-id="element-1216" data-mp="2hntuapctvm7a">
      <div className="w-full bg-ville-navy" data-id="element-1217" data-mp="2hon45z732rtn">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-5 py-2 lg:px-10" data-id="element-1218" data-mp="2hpy7xz0j3zkr">
          <button type="button" aria-label="Search the site" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 p-1 pr-2.5 text-white transition-colors duration-150 ease-ville hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring" data-id="element-1219" data-mp="3fdezp325dfoz">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-ville-spring" aria-hidden="true" data-id="element-1220" data-mp="1wpiejirgy6q" />
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1221" data-mp="3ta2b2auuk8ef" />
          </button>

          <nav aria-label="Utility navigation" className="ml-auto hidden lg:block" data-id="element-1222" data-mp="1rd1m71hbyvm0">
            <ul className="flex items-center gap-5" data-id="element-1223" data-mp="1wcefo52pj0xk">
              {utilityLinks.map(label => <li key={label} data-id="element-1224" data-mp="1ho1g6y4euzyb">
                  <a href="#accueil" className="text-[15px] text-white transition-colors duration-150 ease-ville hover:text-ville-spring" data-id="element-1225" data-mp="1hmysez2dxdmm">
                    {label}
                  </a>
                </li>)}
              <li data-id="element-1226" data-mp="1cox86at8389k">
                <a href="#accueil" lang="fr" className="text-[15px] text-white transition-colors duration-150 ease-ville hover:text-ville-spring" data-id="element-1227" data-mp="1cpggr0wzgkax">
                  Français
                </a>
              </li>
            </ul>
          </nav>

          <Link to="/my-dashboard" className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-full bg-ville-blue px-4 py-1.5 text-[15px] font-bold text-white transition-colors duration-150 ease-ville hover:bg-ville-spring hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring lg:ml-0" data-id="element-1228" data-mp="2t3q4ptxbc718">
            <LeafIcon className="h-4 w-4" aria-hidden="true" data-id="element-1229" data-mp="w5j3h5sss1mp" />
            Online Services
          </Link>
        </div>
      </div>

      <div className="w-full bg-ville-blue" data-id="element-1230" data-mp="rcg94vg74o5g">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-5 py-2.5 lg:px-10" data-id="element-1231" data-mp="154mncbzsgl29">
          <Link to="/" className="flex shrink-0 items-center gap-3 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring" data-id="element-1232" data-mp="106ku4y8nf5d2">
            <Logo variant="icon" data-id="element-1233" data-mp="1a48zsj7slxa0" />
            <span className="font-display text-[19px] font-extrabold text-ville-lime" data-id="element-1234" data-mp="1a5ad6uca6vuw">Home</span>
          </Link>

          <nav aria-label="Main navigation" className="hidden lg:block" onMouseLeave={() => setOpenMenu(null)} data-id="element-1235" data-mp="3gvhyyfsnoi8f">
            <ul className="flex items-center" data-id="element-1236" data-mp="22yxc5bugv6l1">
              {navItems.map(item => {
              const isOpen = openMenu === item.label;
              return <li key={item.label} className="relative" data-id="element-1237" data-mp="27zl4q1ycfvhd">
                    <button type="button" aria-expanded={isOpen} onMouseEnter={() => setOpenMenu(item.label)} onClick={() => setOpenMenu(isOpen ? null : item.label)} className={`flex items-center gap-1 rounded-full px-3 py-2 font-display text-[17px] font-extrabold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring ${isOpen ? 'text-ville-lime' : 'text-white hover:text-ville-lime'}`} data-id="element-1238" data-mp="2cy645z5rub4r">
                      {item.label}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-ville ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1239" data-mp="15j1c04pmc3mx" />
                    </button>

                    {isOpen && <div className="absolute left-0 top-full w-72 pt-3" data-id="element-1240" data-mp="oytyzual5zft">
                        <ul className="rounded-2xl border-2 border-ville-navy bg-white p-2 shadow-xl shadow-ville-navy/25" data-id="element-1241" data-mp="p16n6p6xt61b">
                          {item.children.map(child => <li key={child} data-id="element-1242" data-mp="f27ghc5qfkgy">
                              <a href="#accueil" className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep" data-id="element-1243" data-mp="a9o24cz80lb3">
                                {child}
                              </a>
                            </li>)}
                        </ul>
                      </div>}
                  </li>;
            })}
            </ul>
          </nav>

          <div className="ml-auto flex items-center gap-1" data-id="element-1244" data-mp="3isq4ky5uhv79">
            <a href="#accueil" aria-label="Facebook" className="hidden h-9 w-9 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:text-ville-spring focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring lg:grid" data-id="element-1245" data-mp="3iq3yl2ylrnr5">
              <FacebookIcon className="h-4 w-4" aria-hidden="true" data-id="element-1246" data-mp="5d0rv6925wl7" />
            </a>
            <a href="#accueil" aria-label="YouTube" className="hidden h-9 w-9 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:text-ville-spring focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring lg:grid" data-id="element-1247" data-mp="3mts3tezri798">
              <YoutubeIcon className="h-4 w-4" aria-hidden="true" data-id="element-1248" data-mp="o4bjjpmlyde5" />
            </a>

            <span className="mx-1.5 hidden h-6 w-px bg-white/25 md:block" aria-hidden="true" data-id="element-1249" data-mp="28txdoi5pxlsx" />

            <div className="hidden items-center gap-1 md:flex" data-id="element-1250" data-mp="23v41ct2640uc">
              <Link to={modules[0].to} aria-label="Map" className={moduleButton} data-id="element-1251" data-mp="23uu88wlttdkf">
                <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-1252" data-mp="2iip3ajnb3u0o" />
                <HeaderTooltip label="Map" data-id="element-1253" data-mp="2dkdtg8x0hjig" />
              </Link>
              <Link to={modules[1].to} aria-label="My Card" className={moduleButton} data-id="element-1254" data-mp="2dn9q7tx4i25b">
                <IdCardIcon className="h-5 w-5" aria-hidden="true" data-id="element-1255" data-mp="1pxfdrdgpufll" />
                <HeaderTooltip label="My Card" data-id="element-1256" data-mp="1kxjjw71qg4jr" />
              </Link>

              <div className="relative" data-id="element-1257" data-mp="1ywix8ijsox0m">
                <button type="button" aria-label="My notifications, 3 unread including one urgent alert" aria-expanded={bellOpen} onClick={() => setBellOpen(v => !v)} className={moduleButton} data-id="element-1258" data-mp="1yxkkdle359yp">
                  <BellIcon className="h-5 w-5" aria-hidden="true" data-id="element-1259" data-mp="369b8m35xf06z" />
                  <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-ville-coral px-1 text-[11px] font-bold text-white ring-2 ring-ville-blue" aria-hidden="true" data-id="element-1260" data-mp="13nyuhsw9q67g">
                    3
                  </span>
                  {!bellOpen && <HeaderTooltip label="My notifications" data-id="element-1261" data-mp="1id4s5cfgjod8" />}
                </button>
                <AnimatePresence data-id="element-1262" data-mp="1mcvorwro0ypo">
                  {bellOpen && <BellPreview onNavigate={() => setBellOpen(false)} data-id="element-1263" data-mp="1rc8bmhije5s9" />}
                </AnimatePresence>
              </div>

              <Link to={modules[2].to} aria-label="My Dashboard" className={moduleButton} data-id="element-1264" data-mp="1wbbi86e60k2c">
                <SmartphoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-1265" data-mp="21axv2eq2zyam" />
                <HeaderTooltip label="My Dashboard" data-id="element-1266" data-mp="1xk0skplgvwc" />
              </Link>
              <Link to={modules[3].to} aria-label="My Profile" className={moduleButton} data-id="element-1267" data-mp="1zeog6s93244">
                <UserRoundIcon className="h-5 w-5" aria-hidden="true" data-id="element-1268" data-mp="6wwxdx69phc4" />
                <HeaderTooltip label="My Profile" data-id="element-1269" data-mp="2k7ybie3exu1w" />
              </Link>
            </div>

            <button type="button" aria-label={mobileOpen ? 'Close the menu' : 'Open the menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)} className="grid h-10 w-10 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring lg:hidden" data-id="element-1270" data-mp="2ffy4k1oxe0vt">
              {mobileOpen ? <XIcon className="h-5 w-5" data-id="element-1271" data-mp="2u545pv7bq16c" /> : <MenuIcon className="h-5 w-5" data-id="element-1272" data-mp="2u41cvhqfxons" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && <nav aria-label="Mobile navigation" className="bg-ville-deep px-5 pb-6 lg:hidden" data-id="element-1273" data-mp="2p5rivaggcu3r">
          <ul className="grid gap-2 py-4 md:hidden" data-id="element-1274" data-mp="1iriem4hwlpi6">
            {modules.map(module => <li key={module.label} data-id="element-1275" data-mp="1dp9kh4taoheh">
                <Link to={module.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 text-[15px] font-bold text-white" data-id="element-1276" data-mp="1d2xfjeeus8au">
                  <module.Icon className="h-5 w-5" aria-hidden="true" data-id="element-1277" data-mp="1mtiljmwuwf3s" />
                  {module.label}
                </Link>
              </li>)}
          </ul>
          <ul className="divide-y divide-white/10" data-id="element-1278" data-mp="1vsvvegow75p1">
            {navItems.map(item => <li key={item.label} className="py-3" data-id="element-1279" data-mp="1quu2z55ktu0w">
                <p className="font-display text-sm font-extrabold uppercase tracking-wide text-ville-lime" data-id="element-1280" data-mp="1qu2ley8tdwtm">
                  {item.label}
                </p>
                <ul className="mt-2 space-y-1" data-id="element-1281" data-mp="20s8kl838kous">
                  {item.children.map(child => <li key={child} data-id="element-1282" data-mp="bcsey2oitl9n">
                      <a href="#accueil" className="block py-1.5 text-[15px] text-white/80" data-id="element-1283" data-mp="bdtwmq01l60c">
                        {child}
                      </a>
                    </li>)}
                </ul>
              </li>)}
          </ul>
          <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-4" data-id="element-1284" data-mp="2mzvu34hrxxwp">
            {utilityLinks.map(label => <li key={label} data-id="element-1285" data-mp="289wztctcb6mi">
                <a href="#accueil" className="text-sm text-white/80" data-id="element-1286" data-mp="2dauhloaftynh">
                  {label}
                </a>
              </li>)}
            <li data-id="element-1287" data-mp="1z9rsf2o5c88c">
              <a href="#accueil" lang="fr" className="text-sm text-white/80" data-id="element-1288" data-mp="1zdflk0poismj">
                Français
              </a>
            </li>
          </ul>
        </nav>}
    </header>;
}